// Custom React Hook for User Access Control
import { useState, useEffect, useCallback, useRef } from "react";
import {
	canEmailWrite,
	getUserAccessInfo,
	isEmailAuthorized,
} from "../utils/authorizationUtils";
import userAccessService from "../services/userAccessService";

// How often to re-check access while the tab is visible (15 minutes)
const ACCESS_RECHECK_INTERVAL = 15 * 60 * 1000;
// Re-check access if the tab was hidden for more than this long (5 minutes)
const VISIBILITY_RECHECK_THRESHOLD = 5 * 60 * 1000;

/**
 * Hook to check user's write access
 * @param {string} userEmail - User's email address
 * @returns {Object} - { canWrite, canEditProtected, isLoading, userAccess, refreshAccess }
 */
export const useUserAccess = (userEmail) => {
	const [canWrite, setCanWrite] = useState(false);
	const [canEditProtected, setCanEditProtected] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [userAccess, setUserAccess] = useState(null);

	// Track when the tab was hidden so we know if a long inactivity occurred
	const hiddenAtRef = useRef(null);

	const checkAccess = useCallback(
		async (forceRefresh = false) => {
			if (!userEmail) {
				setCanWrite(false);
				setCanEditProtected(false);
				setIsLoading(false);
				return;
			}

			// If forcing a refresh, clear the cache so we fetch from the API fresh
			if (forceRefresh) {
				userAccessService.clearCache(userEmail);
			}

			setIsLoading(true);

			try {
				// Get full user access info
				const accessInfo = await getUserAccessInfo(userEmail);
				setUserAccess(accessInfo);

				// Check if user can write (from database OR hardcoded list)
				const writeAccess = await canEmailWrite(userEmail);
				setCanWrite(writeAccess);

				// For protected fields, use the same write access check
				setCanEditProtected(writeAccess);

				console.log("✅ User access loaded:", {
					email: userEmail,
					canWrite: writeAccess,
					canEditProtected: writeAccess,
					forceRefresh,
					accessInfo,
				});
			} catch (error) {
				console.error("❌ Error checking user access:", error);
				setCanWrite(false);
				setCanEditProtected(false);
				setUserAccess(null);
			} finally {
				setIsLoading(false);
			}
		},
		[userEmail],
	);

	// Initial load
	useEffect(() => {
		checkAccess();
	}, [checkAccess]);

	// Periodic re-check every ACCESS_RECHECK_INTERVAL while tab is visible
	useEffect(() => {
		if (!userEmail) return;

		const interval = setInterval(() => {
			if (!document.hidden) {
				console.log("🔄 Periodic access re-check");
				checkAccess(true);
			}
		}, ACCESS_RECHECK_INTERVAL);

		return () => clearInterval(interval);
	}, [userEmail, checkAccess]);

	// Re-check when the tab becomes visible after a long period of inactivity
	useEffect(() => {
		if (!userEmail) return;

		const handleVisibilityChange = () => {
			if (document.hidden) {
				hiddenAtRef.current = Date.now();
			} else {
				const hiddenDuration = hiddenAtRef.current
					? Date.now() - hiddenAtRef.current
					: 0;
				hiddenAtRef.current = null;

				if (hiddenDuration > VISIBILITY_RECHECK_THRESHOLD) {
					console.log(
						`🔄 Tab was hidden for ${Math.round(hiddenDuration / 1000)}s — re-checking access`,
					);
					checkAccess(true);
				}
			}
		};

		// Also re-check when the window regains focus (e.g. user switches back from another app)
		const handleFocus = () => {
			const hiddenDuration = hiddenAtRef.current
				? Date.now() - hiddenAtRef.current
				: 0;
			if (hiddenDuration > VISIBILITY_RECHECK_THRESHOLD) {
				console.log("🔄 Window focused after inactivity — re-checking access");
				checkAccess(true);
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("focus", handleFocus);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("focus", handleFocus);
		};
	}, [userEmail, checkAccess]);

	// Function to manually refresh access (useful after permission changes)
	const refreshAccess = useCallback(() => {
		checkAccess(true);
	}, [checkAccess]);

	return {
		canWrite,
		canEditProtected,
		isLoading,
		userAccess,
		refreshAccess,
	};
};

/**
 * Hook to check if user can edit a specific field
 * @param {string} userEmail - User's email address
 * @param {string} fieldName - Name of the field to check
 * @param {boolean} isProtectedField - Whether the field is protected
 * @returns {Object} - { canEdit, isLoading, reason }
 */
export const useFieldAccess = (
	userEmail,
	fieldName,
	isProtectedField = false,
) => {
	const { canWrite, canEditProtected, isLoading } = useUserAccess(userEmail);

	const canEdit = isProtectedField ? canEditProtected : canWrite;

	let reason = "";
	if (!canWrite && !isProtectedField) {
		reason = "You have read-only access";
	} else if (isProtectedField && !canEditProtected) {
		reason = "This is a protected field requiring special authorization";
	}

	return {
		canEdit,
		isLoading,
		reason,
	};
};

export default useUserAccess;
